import { z } from "zod";
/**
 * ESPM import commit schemas — the shared contract for
 * apps/api/src/routes/places/espmImport.ts (mounted OUTSIDE the typed Hono chain,
 * bank-import precedent: large array schemas blow the RPC inference budget).
 *
 * Two sequential commits: properties (→ espm_import_properties RPC, returns
 * the PropertyId → place_id map) then accounts (Pattern B upsert on
 * (org,'espm',MeterId)). Readings reuse the EXISTING readings/bulk endpoint +
 * bulkUtilityReadingsSchema — no third schema here.
 *
 * The wizard sends only reviewed, resolved values: classifications are
 * canonical vocabulary values, meter types are place_meter_types ids, detail
 * values are typed and GFA is already m². Key-presence semantics: optional
 * fields are OMITTED (never null) so re-imports preserve what the file
 * doesn't carry.
 */
const dateString = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "expected a YYYY-MM-DD date");
const externalId = z.string().min(1).max(200);
// ---------------------------------------------------------------------------
// Properties commit (batch ≤ 200 — mirrors the RPC's cap)
// ---------------------------------------------------------------------------
export const espmImportDetailSchema = z
    .object({
    definition_key: z.string().min(1).max(200),
    current_as_of: dateString,
    value_numeric: z.number().finite().optional(),
    value_text: z.string().max(4000).optional(),
    value_boolean: z.boolean().optional(),
    value_date: dateString.optional(),
    unit: z.string().max(50).optional(),
    is_estimated: z.boolean().optional(),
})
    .refine((d) => [d.value_numeric, d.value_text, d.value_boolean, d.value_date].filter((v) => v !== undefined).length === 1, { message: "exactly one typed value is required" });
/**
 * A CONFIRMED Azure Maps match for one ESPM address, carried only for rows the
 * user ticked on the Addresses step. Never the raw ESPM strings: the wizard
 * geocodes first and the user reviews the match, which is what makes writing a
 * gis_locations row defensible (phase-8 Decision 2 refused unvalidated ones).
 *
 * Absent — never null — when this run has no address to offer for the property
 * (excluded, no match, or the place already has one). The RPC then leaves
 * gis_location_id alone, so a re-import can never clear an address.
 */
export const espmImportLocationSchema = z.object({
    formatted_address: z.string().min(1).max(500),
    latitude: z.number().finite().gte(-90).lte(90),
    longitude: z.number().finite().gte(-180).lte(180),
    street_number: z.string().max(100).optional(),
    street_name: z.string().max(300).optional(),
    city: z.string().max(200).optional(),
    state_province: z.string().max(200).optional(),
    postal_code: z.string().max(40).optional(),
    country: z.string().max(200).optional(),
    /** ISO 3166-1 alpha-2 — gis_locations.country_code is varchar(2). */
    country_code: z.string().length(2).optional(),
    /** Azure's place id: the primary dedupe key, org-scoped, when present. */
    azure_place_id: z.string().max(200).optional(),
    /** Match score 0–1 — gis_locations.confidence_score is numeric(3,2). */
    confidence_score: z.number().gte(0).lte(1).optional(),
    /** The ESPM address string that produced this match (provenance). */
    original_input: z.string().max(500).optional(),
});
export const espmImportPropertySchema = z.object({
    external_id: externalId,
    /** Adopt an existing place (review-step "link"): stamp ids, never rename. */
    link_place_id: z.string().uuid().optional(),
    name: z.string().min(1).max(500).optional(),
    parent_external_id: externalId.optional(),
    premises_level: z.enum(["site", "building"]).optional(),
    /** Canonical occupancy value (resolved client-side; FK enforces validity). */
    classification: z.string().min(1).max(200).optional(),
    construction_status: z.string().max(50).optional(),
    ubid: z.string().max(200).optional(),
    /** Confirmed geocoded address; the RPC never overwrites an existing one. */
    location: espmImportLocationSchema.optional(),
    details: z.array(espmImportDetailSchema).max(100).optional(),
});
const duplicateExternalId = (items) => {
    const seen = new Set();
    for (const item of items) {
        if (seen.has(item.external_id))
            return item.external_id;
        seen.add(item.external_id);
    }
    return null;
};
export const espmImportPropertiesSchema = z.object({
    properties: z
        .array(espmImportPropertySchema)
        .min(1)
        .max(200)
        // (org, 'espm', external_id) is UNIQUE and the batch is one statement — a
        // duplicate PropertyId would 23505 the whole commit.
        .refine((items) => duplicateExternalId(items) === null, (items) => ({
        message: `duplicate PropertyId in one batch: ${duplicateExternalId(items)}`,
    })),
});
// ---------------------------------------------------------------------------
// Accounts commit (batch ≤ 500)
// ---------------------------------------------------------------------------
export const espmImportAccountSchema = z
    .object({
    /** ESPM MeterId — upsert key with (org, 'espm'). */
    external_id: externalId,
    /** PropertyId — resolved to place_id via the properties-commit id map. */
    place_external_id: externalId,
    /** Adopt an existing account instead of creating (review-step "link"). */
    link_account_id: z.string().uuid().optional(),
    /** 200, not 500: `place_utility_accounts_name_check` caps at 200 and the
     * creates go out as ONE multi-row INSERT, so a single over-length ESPM
     * name (they are routinely "<Property> - <Meter> - <Utility> (<Acct #>)")
     * would fail the whole district's accounts with a raw constraint error.
     * Truncation happens client-side where the row can be reported. */
    name: z.string().min(1).max(200),
    meter_type_id: z.string().uuid(),
    domain: z.enum(["energy", "water"]),
    /** Resolved shim token (e.g. KGal_US), not the raw display string.
     * 40 = `place_utility_accounts_source_unit_check`. */
    source_unit: z.string().max(40).optional(),
    /**
     * ESPM owns only 'espm_metrics' in the reason vocabulary. `.nullable()` is
     * load-bearing: "Count it" serializes an explicit null (the only way the
     * server's clear rule can fire) — file-included rows always send null,
     * foreign-reason rows OMIT the key entirely. The server applies the
     * ownership rule; it never writes a payload reason blindly over a stored
     * foreign reason.
     */
    rollup_exclusion_reason: z.enum(["espm_metrics"]).nullable().optional(),
    /** Sub-meter of an in-file sibling (its ESPM MeterId; two-pass resolve). */
    parent_external_id: externalId.optional(),
    /**
     * Sub-meter of an already-imported account on the place. Explicit null =
     * actively un-parent (a tri-state changed away from a stored-parent
     * default); omitted = leave any stored parent untouched.
     */
    parent_account_id: z.string().uuid().nullable().optional(),
    /** ESPM provenance riding in metadata (metrics flag, active dates). */
    metadata: z
        .object({
        espm_included_in_metrics: z.boolean().optional(),
        espm_active: z.boolean().optional(),
        espm_first_entry: dateString.optional(),
        espm_last_entry: dateString.optional(),
    })
        .optional(),
})
    .refine((a) => !(a.parent_external_id != null && a.parent_account_id != null), {
    message: "at most one parent (in-file or existing) may be supplied",
})
    .refine((a) => !((a.parent_external_id != null || a.parent_account_id != null) &&
    a.rollup_exclusion_reason != null), { message: "a parent and a non-null exclusion reason are mutually exclusive" });
export const espmImportAccountsSchema = z.object({
    accounts: z
        .array(espmImportAccountSchema)
        .min(1)
        .max(500)
        // Same UNIQUE + single-statement reasoning as properties: a campus meter
        // listed against two properties appears twice in a Custom Download.
        .refine((items) => duplicateExternalId(items) === null, (items) => ({
        message: `duplicate MeterId in one batch: ${duplicateExternalId(items)}`,
    })),
});
//# sourceMappingURL=espmImport.js.map